/**
 * @module app.pp
 */

import { autoinject, bindable, observable, TaskQueue } from 'aurelia-framework';
import { ComponentBase, IForm } from 'app/shared/lib/essette-site';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { AuthService } from 'app/um/services/auth-service';
import { AuthPortalPageContext } from 'app/pp/lib/auth-portal-page-context';
import { validate } from 'app/shared/elements/ess-rule';
import { ExistingContact } from 'app/pp/components/submit/existing-contact';
import { allActionsSucceeded, apiCall } from 'app/shared/lib/essette-api';
import { getReference } from 'app/shared/models/essette-types';
import { Toaster } from 'app/shared/lib/toaster';
import { MyAuthorizationsReloadEvent, SaveAuthDetailsEvent } from 'app/pp/lib/events';
import { IServiceGridOptions, ServiceGrid } from 'app/um/components/auth/service-grid';
import { ISaveServiceOptions } from 'app/um/components/auth/save-service';
import { IAuthMedicationsGridOptions } from 'app/um/components/auth/medications/auth-medications-grid';
import { ISaveAuthMedicationOptions } from 'app/um/components/auth/medications/save-auth-medication';
import { AuthMedications } from 'app/um/components/auth/medications/auth-medications';
import { ActionOptions, IActionInfo } from 'app/shared/lib/api/api-types';
import { I18N } from 'aurelia-i18n';
import { CustomBatchService } from 'app/core/services/custom-batch-service';
import { getFocusSelector, IReturnFocus, returnFocus } from 'app/shared/lib/focus';
import { microTask } from 'app/shared/lib/decorators';
import { WizardNavigator } from 'app/core/components/wizard/wizard-navigator';
import { IWizardStep } from 'app/core/components/wizard/interfaces';
import { EssAccordionCustomAttribute } from 'app/shared/attributes/ess-collapse-attributes';
import { fireEvents } from 'app/shared/lib/fire-events';
import { AuthMedicationReloadEvent } from 'app/um/lib/events';
import * as UM from 'app/um/models/essette-api';
import * as CD from 'app/cd/models/essette-api';

export interface IServiceChange {
    type: ActionOptions.Insert | ActionOptions.Update | ActionOptions.Deactivate;
    service: UM.AuthService;
}

export interface IMedicationChange {
    type: ActionOptions.Insert | ActionOptions.Update | ActionOptions.Deactivate;
    medication: UM.AuthMedication;
}

export interface IAuthDetailsChanges {
    preferredContact?: UM.AuthPreferredContact;
    serviceChanges?: IServiceChange[];
    dentalServiceChanges?: IServiceChange[];
    medicationChanges?: IMedicationChange[];
}

@autoinject()
export class UpdateAuthDetailsStep extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    @bindable({ changeHandler: 'configure' })
    protected auth: UM.Auth | null = null;

    @bindable protected readonly: boolean = false;

    protected showServicesGrid: boolean = false;
    protected showDentalServicesGrid: boolean = false
    protected showMedicationsGrid: boolean = false;

    protected hasPreferredContact: boolean = false;
    protected editContact: UM.AuthPreferredContact = {};
    protected existingContact: CD.ProviderContact = {};

    protected pendingChanges: IAuthDetailsChanges = {};

    protected insertedAuthServiceIds: string[] = [];
    protected insertedAuthMedicationIds: string[] = [];

    @observable
    protected preferredContactOption: string = 'existing';

    protected validate = validate;

    protected subscriptions: Subscription[] = [];

    protected isSingleChangeOnly: boolean = false;
    protected singleChangeMade: boolean = false;

    protected serviceGridOptions: IServiceGridOptions = {
        canOpenServiceStatusHistory: false,
        canSyncToClaimsSystems: false,
        disableEditStatusApprovedOrDenied: true,
        showDueDate: false,
        showServiceStatusReason: true,
        canApplyBulkServiceStatus: false,
        canApplyCorrespondenceRules: false,
        showExternalActions: false,
        showApprovedWithRequestedCharges: true,
        canSyncToClaimsSystemsLineLevel: false,
        showExistingAuthsWarning: false,
        showDuplicateAuths: false,
        showAddDocumentButton: false,
        showOpenIqcSummary: false,
        showWarnings: true,
        showModifiers: false,
        portalEditMode: true,
        pendServiceChanges: true
    };

    protected dentalServiceGridOptions: IServiceGridOptions = {
        canOpenServiceStatusHistory: false,
        canSyncToClaimsSystems: false,
        disableEditStatusApprovedOrDenied: true,
        showDueDate: false,
        showServiceStatusReason: true,
        canApplyBulkServiceStatus: false,
        canApplyCorrespondenceRules: false,
        showExternalActions: false,
        showApprovedWithRequestedCharges: true,
        canSyncToClaimsSystemsLineLevel: false,
        showExistingAuthsWarning: false,
        showDuplicateAuths: false,
        showAddDocumentButton: false,
        showOpenIqcSummary: false,
        showWarnings: true,
        showModifiers: false,
        portalEditMode: true,
        pendServiceChanges: true
    };

    protected saveServiceOptions: ISaveServiceOptions = {
        showStatus: false,
        showStatusReason: false,
        showQtyApproved: false,
        showApprovedCharges: false,
        showModifiers: false,
        portalEditMode: true,
        pendServiceChanges: true
    };

    protected saveDentalServiceOptions: ISaveServiceOptions = {
        showStatus: false,
        showStatusReason: false,
        showQtyApproved: false,
        showApprovedCharges: false,
        showModifiers: false,
        portalEditMode: true,
        pendServiceChanges: true
    };

    protected authMedicationsGridOptions: IAuthMedicationsGridOptions = {
        showStatusReason: true,
        showQtyApproved: false,
        showDaysApproved: false,
        showApprovedWithRequestedCharges: true,
        showWarnings: true,
        portalEditMode: true,
        pendMedicationChanges: true
    };

    protected saveAuthMedicationOptions: ISaveAuthMedicationOptions = {
        showStatusReason: false,
        showQuantityApproved: false,
        showUnitApproved: false,
        showDaysApproved: false,
        showApprovedCharges: false,
        portalEditMode: true,
        pendMedicationChanges: true
    };

    protected lowerCaseServiceLabel: string = this.i18n.tr('um:service.$').toLowerCase();
    protected lowerCaseMedicationLabel: string = this.i18n.tr('um:medication.$').toLowerCase();
    protected lowerCaseDentalServiceLabel: string = this.i18n.tr('um:service.dental.$long').toLowerCase();
    protected latestStepIndex: number = 0;

    ////****************** Element References ***************************************////
    protected $existingContact!: ExistingContact;
    protected $serviceGrid!: ServiceGrid;
    protected $dentalServiceGrid!: ServiceGrid;
    protected $authMedications!: AuthMedications;
    protected $collapsible!: EssAccordionCustomAttribute;
    protected $wizard!: WizardNavigator;
    protected $steps: IWizardStep[] = [];

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: AuthService,
        private readonly customBatchService: CustomBatchService,
        private readonly context: AuthPortalPageContext,
        private readonly toaster: Toaster,
        private readonly events: EventAggregator,
        private readonly taskQueue: TaskQueue,
        private readonly i18n: I18N,
        private $element: Element,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        if(!this.auth?.id) this.logger.warn('Bindable property [authId] was not set at binding');
        this.loadProviderRequestedChangesStatus();
        this.checkPermissions();
    }

    protected attached(): void {
        this.subscriptions.push(
            this.events.subscribe(SaveAuthDetailsEvent, (e: SaveAuthDetailsEvent) => {
                this.submit(e.continueUpdate);
            })
        );
    }

    protected detached(): void {
        this.subscriptions.forEach(s => s.dispose());
        this.subscriptions = [];
    }

    ////****************** Events ***************************************************////
    protected preferredContactOptionChanged() {
        // Clear editContact on radial Preferred Contact option change
        this.editContact = {};
        if(this.$existingContact) this.$existingContact.reset();
    }

    ////****************** Functions ************************************************////
    protected async configure(): Promise<void> {
        this.reset();
        this.showServicesGrid = false;
        this.showDentalServicesGrid = false;
        this.showMedicationsGrid = false;
        this.insertedAuthMedicationIds = [];
        this.insertedAuthServiceIds = [];
        if(!this.auth?.id) return;

        // Existing details with missing required fields should still be treated as newly inserted details to allow a user to finish the details on the update modal request.
        this.insertedAuthServiceIds.push(...this.context.missingFieldExistingServiceIds);
        this.insertedAuthMedicationIds.push(...this.context.missingFieldExistingMedicationIds);

        this.showServicesGrid = !!this.context.settings.services.visible && (!!this.context.settings.servicesExpDate.editableInPortal || !!this.context.settings.serviceRequestedQuantity.editableInPortal || !!this.context.settings.serviceRequestedCharges.editableInPortal);
        this.showDentalServicesGrid = !!this.context.settings.servicesDental.visible && (!!this.context.settings.servicesDentalExpDate.editableInPortal || !!this.context.settings.serviceDentalRequestedQuantity.editableInPortal);
        this.showMedicationsGrid = !!this.context.settings.drugs.visible && (!!this.context.settings.drugRequestedQuantity.editableInPortal || !!this.context.settings.drugRequestedCharges.editableInPortal || !!this.context.settings.drugRequestedDays.editableInPortal || !!this.context.settings.drugsExpDate.editableInPortal);
        this.serviceGridOptions.showModifiers = this.saveServiceOptions.showModifiers = this.context?.settings?.servicesModifier?.visible ?? false;
        this.dentalServiceGridOptions.showModifiers = this.saveDentalServiceOptions.showModifiers = this.context?.settings?.servicesDentalModifier?.visible ?? false;
    }

    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.hasPreferredContact = !!this.accountContext.hasPermission('UM.Auth.Update.PreferredContact');
        this.authMedicationsGridOptions.canDeleteMedications = this.dentalServiceGridOptions.canDeleteServices = this.serviceGridOptions.canDeleteServices = !!this.accountContext.hasPermission('UM.Auth.DeleteDetails.DeactivateAuthDetails');
    }

    //#region Preferred Contact Builders and Helpers

    protected buildContactWithProviderContact(): UM.AuthPreferredContact {
        const contact: UM.AuthPreferredContact = {
            auth: getReference(this.auth),
            name: this.existingContact.name?.full,
            emailAddress: this.existingContact.emailAddress ? this.existingContact.emailAddress : undefined,
            phone: this.existingContact.phoneNumber?.primary?.number ?
                {
                    number: this.existingContact.phoneNumber.primary.number,
                    extension: this.existingContact.phoneNumber.primary.extension,
                } : undefined,
            fax: {
                number: this.existingContact.phoneNumber?.fax?.number,
                extension: this.existingContact.phoneNumber?.fax?.extension
            }
        };

        return contact;
    }

    protected buildContact(): UM.AuthPreferredContact {
        const contact: UM.AuthPreferredContact = {
            auth: getReference(this.auth),
            name: this.editContact.name,
            emailAddress: this.editContact.emailAddress && this.editContact.emailAddress.trim().length > 0 ? this.editContact.emailAddress : undefined,
            phone: this.editContact.phone?.number ?
                {
                    number: this.editContact.phone.number,
                    extension: this.editContact.phone?.extension && this.editContact.phone.extension.trim().length > 0 ? this.editContact.phone.extension : null,
                } : undefined,
            fax: this.editContact.fax?.number ?
                {
                    number: this.editContact.fax.number,
                    extension: this.editContact.fax?.extension && this.editContact.fax.extension.trim().length > 0 ? this.editContact.fax.extension : null,
                } : undefined
        };

        return contact;
    }

    protected resetPreferredContact(contactSaved: boolean = false): void {
        this.pendingChanges.preferredContact = undefined;
        if(this.$existingContact) {
            // if contact is saved, resetting Existing Contact component should not reselect any previous selections
            this.$existingContact.contactSaved = contactSaved;
            this.$existingContact.reset();
        }
        this.existingContact = {};
        this.editContact = {};
        this.preferredContactOption = 'existing';
    }

    protected saveToPendingPreferredContact(): void {
        const saveContact = this.preferredContactOption === 'existing' ? this.buildContactWithProviderContact() : this.buildContact();

        this.pendingChanges.preferredContact = saveContact;
        const lowercasePreferredContact = this.i18n.tr('um:authPreferredContact.$').toLowerCase();
        this.toaster.info().message(this.i18n.tr('ui:messages.events.addToPending', { what: lowercasePreferredContact })).show();
    }

    protected cancelPendingPreferredContact(): void {
        // This is the location of the current element's location
        const activeElementPath = getFocusSelector(document.activeElement as HTMLElement)
        this.pendingChanges.preferredContact = undefined;
        const lowercasePreferredContact = this.i18n.tr('um:authPreferredContact.$').toLowerCase();
        this.toaster.info().message(this.i18n.tr('ui:messages.events.deleteFromPending', { what: lowercasePreferredContact })).show();

        // Set focus back to previous element
        this.taskQueue.queueTask(() => {
            if(activeElementPath) {
                returnFocus(activeElementPath);
            }
        });
    }

    protected async savePendingPreferredContact(): Promise<void> {
        if(!this.pendingChanges.preferredContact) return;
        const isSuccessful = await this.savePendingChanges('preferredContact');
        if(isSuccessful) {
            this.resetPreferredContact(true);
        }
    }

    //#endregion

    protected cancelPendingServiceChanges(): void {
        const count = this.pendingChanges?.serviceChanges?.length || 0;
        if(!!this.pendingChanges?.serviceChanges) this.pendingChanges.serviceChanges.splice(0);
        const lowercaseService = this.i18n.tr('um:service.$').toLowerCase();
        const trKey = count > 1 ? 'ui:messages.events.deleteFromPending_plural' : 'ui:messages.events.deleteFromPending';
        this.toaster.info().message(this.i18n.tr(trKey, { what: lowercaseService })).show();
        this.$serviceGrid.loadServices();
        const activeElementPath = getFocusSelector(document.activeElement as HTMLElement);
        this.restoreFocus(activeElementPath);
    }

    protected cancelPendingServiceChange(serviceIndex: number): void {
        if(!this.pendingChanges || !this.pendingChanges.serviceChanges) return;
        if(serviceIndex !== -1) {
            this.pendingChanges.serviceChanges.splice(serviceIndex, 1);
            const lowercaseService = this.i18n.tr('um:service.$').toLowerCase();
            this.toaster.info().message(this.i18n.tr('ui:messages.events.deleteFromPending', { what: lowercaseService })).show();
            this.$serviceGrid.loadServices();
            const activeElementPath = getFocusSelector(document.activeElement as HTMLElement);
            this.restoreFocus(activeElementPath);
        }
    }

    protected cancelPendingMedicationChanges(): void {
        const count = this.pendingChanges?.medicationChanges?.length || 0;
        if(!!this.pendingChanges?.medicationChanges) this.pendingChanges.medicationChanges.splice(0);
        const lowercaseMedication = this.i18n.tr('um:medication.$').toLowerCase();
        const trKey = count > 1 ? 'ui:messages.events.deleteFromPending_plural' : 'ui:messages.events.deleteFromPending';
        this.toaster.info().message(this.i18n.tr(trKey, { what: lowercaseMedication })).show();
        this.events.publish(new AuthMedicationReloadEvent);
        const activeElementPath = getFocusSelector(document.activeElement as HTMLElement);
        this.restoreFocus(activeElementPath);
    }

    protected cancelPendingDentalServiceChanges(): void {
        const count = this.pendingChanges?.dentalServiceChanges?.length || 0;
        if(!!this.pendingChanges?.dentalServiceChanges) this.pendingChanges.dentalServiceChanges.splice(0);
        const lowercaseService = this.i18n.tr('um:service.$').toLowerCase();
        const trKey = count > 1 ? 'ui:messages.events.deleteFromPending_plural' : 'ui:messages.events.deleteFromPending';
        this.toaster.info().message(this.i18n.tr(trKey, { what: lowercaseService })).show();
        this.$dentalServiceGrid.loadServices();
        const activeElementPath = getFocusSelector(document.activeElement as HTMLElement);
        this.restoreFocus(activeElementPath);
    }

    protected cancelPendingMedicationChange(medicationIndex: number): void {
        if(!this.pendingChanges || !this.pendingChanges.medicationChanges) return;
        if(medicationIndex !== -1) {
            this.pendingChanges.medicationChanges.splice(medicationIndex, 1);
            const lowercaseMedication = this.i18n.tr('um:medication.$').toLowerCase();
            this.toaster.info().message(this.i18n.tr('ui:messages.events.deleteFromPending', { what: lowercaseMedication })).show();
            this.events.publish(new AuthMedicationReloadEvent);
            const activeElementPath = getFocusSelector(document.activeElement as HTMLElement);
            this.restoreFocus(activeElementPath);
        }
    }

    protected cancelPendingDentalServiceChange(serviceIndex: number): void {
        if(!this.pendingChanges || !this.pendingChanges.dentalServiceChanges) return;
        if(serviceIndex !== -1) {
            this.pendingChanges.dentalServiceChanges.splice(serviceIndex, 1);
            const lowercaseService = this.i18n.tr('um:service.$').toLowerCase();
            this.toaster.info().message(this.i18n.tr('ui:messages.events.deleteFromPending', { what: lowercaseService })).show();
            this.$dentalServiceGrid.loadServices();
            const activeElementPath = getFocusSelector(document.activeElement as HTMLElement);
            this.restoreFocus(activeElementPath);
        }
    }

    protected getAction(action: string = ''): string {
        let actionString = '';
        switch(action) {
            case ActionOptions.Insert:
                actionString = this.i18n.tr('ui:actions.add');
                break;
            case ActionOptions.Update:
                actionString = this.i18n.tr('ui:actions.edit');
                break;
            case ActionOptions.Deactivate:
                actionString = this.i18n.tr('ui:actions.delete');
                break;
            default:
                actionString = '';
        }
        return actionString;
    }

    protected buildBatchCalls(section: string = ''): IActionInfo[] {
        const calls: IActionInfo[] = [];

        // Preferred Contact Change
        if(!!this.pendingChanges.preferredContact && (!section || section === 'preferredContact')) {
            const actionInfo = this.service.saveAuthPreferredContactActionInfo(this.pendingChanges.preferredContact);
            calls.push(actionInfo);
        }
        // Service Changes
        if(!!this.pendingChanges.serviceChanges && (!section || section === 'services')) {
            const serviceChanges = this.pendingChanges.serviceChanges;
            if(!!serviceChanges.length) {
                const addOrUpdateServiceChanges = serviceChanges.filter(change => change.type === ActionOptions.Insert || change.type === ActionOptions.Update);
                for(const change of addOrUpdateServiceChanges) {
                    const actionInfo = this.service.saveAuthServiceActionInfo(change);
                    calls.push(actionInfo);
                }

                const deleteServiceChanges = serviceChanges.filter(change => change.type === ActionOptions.Deactivate);
                for(const change of deleteServiceChanges) {
                    const actionInfo = this.service.activateAuthServiceActionInfo(change, false, change.service?.metadata?.activeChanged?.deactivatedReason || 'N/A');
                    calls.push(actionInfo);
                }
            }
        }
        // Dental Service Changes
        if(!!this.pendingChanges.dentalServiceChanges && (!section || section === 'dentalServices')) {
            const dentalServiceChanges = this.pendingChanges.dentalServiceChanges;
            if(!!dentalServiceChanges.length) {
                const addOrUpdateDentalServiceChanges = dentalServiceChanges.filter(change => change.type === ActionOptions.Insert || change.type === ActionOptions.Update);
                for(const change of addOrUpdateDentalServiceChanges) {
                    const actionInfo = this.service.saveAuthServiceActionInfo(change);
                    calls.push(actionInfo);
                }

                const deleteDentalServiceChanges = dentalServiceChanges.filter(change => change.type === ActionOptions.Deactivate);
                for(const change of deleteDentalServiceChanges) {
                    const actionInfo = this.service.activateAuthServiceActionInfo(change, false, change.service?.metadata?.activeChanged?.deactivatedReason || 'N/A');
                    calls.push(actionInfo);
                }
            }
        }
        // Medication Changes
        if(!!this.pendingChanges.medicationChanges && (!section || section === 'medications')) {
            const medicationChanges = this.pendingChanges.medicationChanges;
            if(!!medicationChanges.length) {
                const addOrUpdateMedicationChanges = medicationChanges.filter(change => change.type === ActionOptions.Insert || change.type === ActionOptions.Update);
                for(const change of addOrUpdateMedicationChanges) {
                    const actionInfo = this.service.saveAuthMedicationActionInfo(change);
                    calls.push(actionInfo);
                }

                const deleteMedicationChanges = medicationChanges.filter(change => change.type === ActionOptions.Deactivate && !!change.medication.id);
                for(const change of deleteMedicationChanges) {
                    if(!change.medication.id) continue;

                    const actionInfo = this.service.activateAuthMedicationActionInfo(change, false, change.medication?.metadata?.activeChanged?.deactivatedReason || 'N/A');
                    calls.push(actionInfo);
                }
            }

        }

        return calls;
    }

    ////

    protected isSectionEnabled(item: Element): boolean {
        if(!this.auth?.id) return false;

        return this.$wizard?.isSectionEnabled(item);
    }

    protected submitAuthDetailUpdates(): void {
        const detail = {
            auth: this.auth
        };
        fireEvents(this.$element, ['auth-details-updated'], detail);
    }

    ////****************** IForm ****************************************************////

    hasChanges(): boolean {
        return !!this.pendingChanges.preferredContact
            || !!this.pendingChanges.serviceChanges?.length
            || !!this.pendingChanges.dentalServiceChanges?.length
            || !!this.pendingChanges.medicationChanges?.length;
    }

    protected get singleChangePending(): boolean {
        return !!this.pendingChanges.serviceChanges?.length
            || !!this.pendingChanges.dentalServiceChanges?.length
            || !!this.pendingChanges.medicationChanges?.length;
    }

    public async submit(continueUpdate: boolean = false): Promise<boolean> {
        if(!this.pendingChanges.preferredContact && !this.pendingChanges.serviceChanges?.length && !this.pendingChanges.dentalServiceChanges?.length && !this.pendingChanges.medicationChanges?.length && !continueUpdate) {
            // No Pending Changes to Save
            this.toaster.warn().message(this.i18n.tr('um:local.warning.noPendingChangesToSave')).show();
            return Promise.resolve(false);
        }

        return await this.savePendingChanges();
    }

    reset(): void {
        this.pendingChanges = {};
        this.resetPreferredContact();
    }

    @microTask
    protected restoreFocus(activeElementPath: IReturnFocus): void {
        if(activeElementPath) returnFocus(activeElementPath);
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    protected async savePendingChanges(section: string = ''): Promise<boolean> {
        let isSuccessful = false;
        const calls = this.buildBatchCalls(section);
        if(calls.length === 0) return true; // Nothing to save
        const activeElementPath = getFocusSelector(document.activeElement as HTMLElement);
        try {
            this.state.beginSave();

            const progressToast = this.toaster.standard.batch('ui:labels.pendingChange.$short', calls.length);
            progressToast.show();

            const responses = await this.customBatchService.executeBatchCalls({ calls, progress: progressToast });

            // Newly Inserted Service and Medication Ids, used to filter for details for user to be able to delete.
            responses.filter(r => r.action === 'insert' && !!r.id).forEach(r => {
                if(r.model === 'UM.AuthService') {
                    this.insertedAuthServiceIds.push(r.id!);
                }
                if(r.model === 'UM.AuthMedication') {
                    this.insertedAuthMedicationIds.push(r.id!);
                }
            });

            isSuccessful = allActionsSucceeded(responses);
        }
        catch(error) {
            this.logger.error('Error saving pending auth changes', error);
            throw error;
        }
        finally {
            this.state.endSave();

            if(isSuccessful) {
                this.events.publish(new MyAuthorizationsReloadEvent());
                this.toaster.standard.saved('ui:labels.pendingChange.$short', isSuccessful, calls.length);

                // If the preferred contact changes were made, reload information
                if(!!this.pendingChanges.preferredContact && (!section || section === 'preferredContact')) {
                    await this.reloadPreferredContact();
                    this.resetPreferredContact(true);
                }
                // Clear Pending Services
                if(this.pendingChanges.serviceChanges && (!section || section === 'services')) {
                    this.pendingChanges.serviceChanges.splice(0);
                    this.$serviceGrid.loadServices();
                    this.singleChangeMade = true;
                }
                // Clear Pending Dental Services
                if(this.pendingChanges.dentalServiceChanges && (!section || section === 'dentalServices')) {
                    this.pendingChanges.dentalServiceChanges.splice(0);
                    this.$dentalServiceGrid.loadServices();
                    this.singleChangeMade = true;
                }
                // Clear Pending Medications
                if(this.pendingChanges.medicationChanges && (!section || section === 'medications')) {
                    this.pendingChanges.medicationChanges.splice(0);
                    this.events.publish(new AuthMedicationReloadEvent);
                    this.singleChangeMade = true;
                }
                fireEvents(this.$element, ['auth-change'], { authReloaded: false, triggerRules: false });
            }

            this.restoreFocus(activeElementPath);

            return isSuccessful;
        }
    }

    @apiCall
    protected async reloadPreferredContact(): Promise<void> {
        if(!this.auth?.id) return;

        const includes = [
            'preferredContact'
        ];

        const fields = [
            'preferredContact.emailAddress',
            'preferredContact.fax.*',
            'preferredContact.name',
            'preferredContact.phone.*'
        ];

        try {
            this.state.beginLoad();
            const response = await this.service.loadAuth(this.auth.id, includes, { top: 1 }, fields);
            if(!this.auth?.preferredContact) this.auth = { ...this.auth, preferredContact: {} };
            this.auth.preferredContact = response.results[0].preferredContact;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async loadProviderRequestedChangesStatus(): Promise<void> {
        try {
            this.state.beginLoad();
            const response = await this.service.loadAuthStatuses({ top: 1 }, [], { providerChangesRequested: true }, ['flag.allowEditsInProviderPortal']);
            const providerChangesRequestedStatus = response.results[0];
            if(!providerChangesRequestedStatus?.flag?.allowEditsInProviderPortal) this.isSingleChangeOnly = true;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
